// Gradient Builder - Preset Gallery Component
// Grid of preset gradient thumbnails organized by category

import React from "react";
import { GradientPreset } from "../../../model/gradient";
import { gradientPresets } from "../../../data/gradientPresets";
import { colors, typography, effects } from "../../../design/tokens";

interface GradientPresetGalleryProps {
  onSelectPreset: (preset: GradientPreset) => void;
  selectedPresetId?: string;
  columns?: 2 | 3; // Allow 2 or 3 column grid
}

// Categorize presets by theme
const presetCategories = [
  {
    name: "Vibrant",
    ids: ["sunset-glow", "tropical-horizon", "electric-blue", "neon-pulse", "plasma-burst", "rainbow-wheel"],
  },
  {
    name: "Nature",
    ids: ["ocean-blue", "forest-dawn", "autumn-fire", "northern-lights", "cherry-blossom", "mountain-mist"],
  },
  {
    name: "Dark & Moody",
    ids: ["purple-midnight", "cosmic-dust", "aurora-night", "black-diamond", "midnight-wine", "obsidian-storm"],
  },
  {
    name: "Warm",
    ids: ["sunset-circle", "inferno", "rose-gold", "aurora-mesh"],
  },
];

// Create a map for quick lookup
const presetMap = new Map(gradientPresets.map(p => [p.id, p]));

// Get categorized presets
const getCategorizedPresets = () => {
  return presetCategories.map(category => ({
    name: category.name,
    presets: category.ids
      .map(id => presetMap.get(id))
      .filter((p): p is GradientPreset => p !== undefined),
  }));
};

const PresetButton: React.FC<{
  preset: GradientPreset;
  isSelected: boolean;
  onSelect: () => void;
}> = ({ preset, isSelected, onSelect }) => (
  <button
    onClick={onSelect}
    className={`
      flex flex-col items-center gap-2 p-2 ${effects.borderRadius.lg}
      border ${effects.transition.default}
      ${effects.hover.lift} ${effects.hover.shadow}
      ${
        isSelected
          ? `border-primary-blue ${colors.surface.secondary}`
          : `${colors.border.primary} ${colors.surface.primary}`
      }
    `}
  >
    {/* Gradient Thumbnail */}
    <div
      className={`w-full aspect-video ${effects.borderRadius.md} border ${colors.border.primary} overflow-hidden`}
      style={{
        background: preset.cssPreview,
      }}
    />
    {/* Preset Name */}
    <span className={`${typography.size.xs} ${colors.text.primary} ${typography.weight.medium} text-center`}>
      {preset.name}
    </span>
  </button>
);

export const GradientPresetGallery: React.FC<GradientPresetGalleryProps> = ({
  onSelectPreset,
  selectedPresetId,
  columns = 2,
}) => {
  const categories = getCategorizedPresets();

  return (
    <div className="space-y-6">
      {categories.map((category) => (
        <div key={category.name}>
          {/* Category Header */}
          <div className="flex items-center gap-3 mb-3">
            <span className={`${typography.size.xs} ${colors.text.secondary} ${typography.weight.medium} ${typography.transform.uppercase} ${typography.tracking.wider}`}>
              {category.name}
            </span>
            <div className={`flex-1 h-px ${colors.surface.secondary}`} style={{ backgroundColor: 'var(--dark-border, #2A2E34)' }} />
          </div>

          {/* Presets Grid */}
          <div className={`grid ${columns === 3 ? 'grid-cols-3' : 'grid-cols-2'} gap-2`}>
            {category.presets.map((preset) => (
              <PresetButton
                key={preset.id}
                preset={preset}
                isSelected={preset.id === selectedPresetId}
                onSelect={() => onSelectPreset(preset)}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

